import React, { useState } from 'react';
import {
  Button,
  Typography,
  TextField,
  Grid,
  Box,
  IconButton,
  FormHelperText,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { ReactComponent as NoItem } from '../../../../assets/icon/no-item.svg';
import { grey } from '@mui/material/colors';
import PropTypes from 'prop-types';
import Editor from '@monaco-editor/react';
import { deepClone } from '@mui/x-data-grid/utils/utils';

const TABS = {
  row: 'row',
  json: 'json',
};

const CollapsibleCard = ({
  label,
  environments,
  setFieldValue,
  deleteEnv,
  addNewRow,
  readonly,
  setEnvironments,
}) => {
  const [tab, setTab] = useState(TABS.row);
  const [json, setJson] = useState();

  const convertEnvironmentToJSON = () => {
    try {
      const parsedData = {};
      for (let i in environments) {
        const item = environments[i];
        if (!item.key) continue;
        parsedData[item.key] = item.value;
      }

      setJson(JSON.stringify(parsedData, null, 2));
    } catch (error) {
      console.error('Invalid JSON', error);
    }
  };

  const handleChangeJSON = (newValue) => {
    try {
      const result = [];
      const parseValue = newValue ? JSON.parse(newValue) : {};
      for (let i in parseValue) {
        const key = i;
        const value = parseValue[i];
        result.push({
          id: key,
          key,
          value,
          isValid: true,
          isRequired: false,
        });
      }
      setEnvironments(deepClone(result));
    } catch (error) {
      console.error('error', error);
    }
  };

  return (
    <>
      <Box
        display={'flex'}
        justifyContent={'space-between'}
        alignItems={'center'}
        mb={4}
      >
        <Typography variant='h5'> {label}</Typography>

        <Box
          component='span'
          sx={{
            '& .tabs': {
              background: '#f1f4f9',
              padding: 2,
              pb: 3,
              borderRadius: 2,
              '& .MuiButtonBase-root': {
                color: '#333',
              },
            },

            '& .active': {
              background: '#fff',
              boxShadow: ' rgba(99, 99, 99, 0.2) 0px 2px 8px 0px',
            },
          }}
        >
          <Box component={'span'} className='tabs'>
            <Button
              size='small'
              className={tab === TABS.row ? 'active' : ''}
              onClick={() => setTab(TABS.row)}
            >
              Row
            </Button>
            <Button
              size='small'
              className={tab === TABS.json ? 'active' : ''}
              onClick={() => {
                setTab(TABS.json);
                convertEnvironmentToJSON();
              }}
            >
              JSON
            </Button>
          </Box>
        </Box>
      </Box>
      <Box
        display={'flex'}
        justifyContent={'space-between'}
        alignItems={'center'}
      >
        <FormHelperText sx={{ mb: 2 }}>
          If the key is empty, the system will automatically remove it from the
          environments.
        </FormHelperText>
        {!readonly && tab === TABS.row && (
          <Button onClick={addNewRow}>Add</Button>
        )}
      </Box>

      {tab === TABS.row &&
        environments?.map((env, idx) => (
          <Grid
            key={env.id}
            mt={4}
            mb={4}
            alignItems='start'
            container
            spacing={2}
          >
            <Grid item xs={4}>
              <TextField
                disabled={readonly || env.isRequired}
                required
                fullWidth
                value={env.key}
                inputProps={{ maxLength: 255 }}
                label='Key'
                sx={{ '& .Mui-disabled': { background: grey[50] } }}
                onChange={(e) => {
                  setFieldValue(idx, 'key', e.target.value);
                }}
              />
            </Grid>
            <Grid item xs={readonly ? 8 : 7}>
              <TextField
                disabled={readonly}
                fullWidth
                required
                value={env.value}
                label='Value'
                onChange={(e) => {
                  setFieldValue(idx, 'value', e.target.value);
                }}
              />
            </Grid>
            {!readonly && !env.isRequired && (
              <Grid item xs={1} sx={{ textAlign: 'right' }}>
                <IconButton aria-label='delete' onClick={() => deleteEnv(env)}>
                  <DeleteIcon />
                </IconButton>
              </Grid>
            )}
          </Grid>
        ))}

      {tab === TABS.json && (
        <Editor
          height='600px'
          defaultLanguage='json'
          value={json}
          options={{
            minimap: { enabled: false },
            automaticLayout: true,
            wordWrap: 'on',
            scrollBeyondLastLine: false,
          }}
          onChange={handleChangeJSON}
        />
      )}

      {tab === TABS.row && !environments?.length && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '200px',
          }}
        >
          <NoItem />

          <Typography sx={{ marginTop: 2 }} variant='caption'>
            No item displayed
          </Typography>
        </Box>
      )}
    </>
  );
};

export default React.memo(CollapsibleCard);

CollapsibleCard.propTypes = {
  label: PropTypes.string,
  environments: PropTypes.array,
  setFieldValue: PropTypes.func,
  deleteEnv: PropTypes.func,
  addNewRow: PropTypes.func,
  setEnvironments: PropTypes.func,
  readonly: PropTypes.bool,
};
